import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getDnaReview, saveDnaReview } from "../../lib/api";

const BEHAVIOR_OPTS = [
  { value: "none", label: "N/A" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];

// Shared Review DNA panel. A DNA stays a non-scored "did not attend" record;
// here staff describe what the student actually did during that block and may
// add a behavior score (which DOES count toward the behavior average).
export default function DnaReviewDialog({ open, onOpenChange, date, onSaved, allowDateChange = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});   // id -> {dna_activity, behavior}
  const [savingId, setSavingId] = useState(null);
  const [selDate, setSelDate] = useState(date);

  // Keep the selected date in sync with the incoming prop each time the dialog opens.
  useEffect(() => {
    if (open) setSelDate(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const load = async () => {
    if (!selDate) return;
    setLoading(true);
    try {
      const res = await getDnaReview(selDate);
      setData(res.data);
      const d = {};
      (res.data.items || []).forEach((it) => {
        d[it.id] = {
          dna_activity: it.dna_activity || "",
          behavior: it.behavior != null ? String(it.behavior) : "none",
        };
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load DNA list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selDate) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selDate]);

  const setDraft = (id, patch) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSave = async (item) => {
    const draft = drafts[item.id] || {};
    setSavingId(item.id);
    try {
      await saveDnaReview(item.id, {
        dna_activity: draft.dna_activity || "",
        behavior: draft.behavior,
      });
      toast.success(`Saved review for ${item.student_name}`);
      await load();
      onSaved && onSaved();
    } catch {
      toast.error("Failed to save review");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="dna-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-700">Review DNA — {selDate}</DialogTitle>
          <DialogDescription>
            Did-not-attend blocks (no class or 1:1 was logged). Note what the student did
            and optionally add a behavior score.
          </DialogDescription>
          {allowDateChange && (
            <div className="flex items-center gap-2 pt-2">
              <label className="text-sm font-medium text-slate-600" htmlFor="dna-review-date">Date</label>
              <input
                id="dna-review-date"
                type="date"
                value={selDate || ""}
                onChange={(e) => setSelDate(e.target.value)}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm"
                data-testid="dna-review-date-picker"
              />
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <p className="text-center py-10 text-muted-foreground">Loading…</p>
        ) : !data || data.items.length === 0 ? (
          <p className="text-center py-10 text-green-600" data-testid="dna-review-empty">
            No DNA blocks to review for this date.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span className="px-2 py-1 rounded bg-orange-100 text-orange-700 font-semibold" data-testid="dna-review-needs">
                {data.needs_review} need review
              </span>
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-semibold">
                {data.reviewed} reviewed
              </span>
            </div>

            {data.items.map((item) => {
              const draft = drafts[item.id] || { dna_activity: "", behavior: "none" };
              return (
                <div key={item.id}
                  className={`rounded-lg border p-3 ${item.dna_reviewed ? "border-emerald-200 bg-emerald-50/40" : "border-orange-200 bg-orange-50/40"}`}
                  data-testid={`dna-review-item-${item.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-semibold text-slate-900">{item.student_name}</span>
                      <span className="text-sm text-slate-500 ml-2">
                        {item.block}{item.class_name ? ` · ${item.class_name}` : ""}
                      </span>
                    </div>
                    {item.dna_reviewed && (
                      <span className="text-xs font-semibold text-emerald-700" data-testid={`dna-reviewed-badge-${item.id}`}>Reviewed</span>
                    )}
                  </div>
                  <Textarea
                    value={draft.dna_activity}
                    onChange={(e) => setDraft(item.id, { dna_activity: e.target.value })}
                    placeholder="What did the student do during this block (if known)?"
                    rows={2}
                    className="bg-white"
                    data-testid={`dna-activity-input-${item.id}`}
                  />
                  <div className="flex items-end gap-3 mt-2">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Behavior</label>
                      <Select value={draft.behavior} onValueChange={(v) => setDraft(item.id, { behavior: v })}>
                        <SelectTrigger className="w-24 h-9 bg-white" data-testid={`dna-behavior-select-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BEHAVIOR_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90 ml-auto"
                      disabled={savingId === item.id}
                      onClick={() => handleSave(item)}
                      data-testid={`dna-review-save-${item.id}`}>
                      {savingId === item.id ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
